/**
 * Calendar Event Formatter
 *
 * Utility class for formatting calendar events for display.
 * Handles date/time formatting, section building, and event sorting.
 *
 * Follows single responsibility principle: only responsible for
 * transforming raw event data into UI-friendly formats.
 */

import type { CalendarEvent } from '@/types/events';
import type {
  CalendarAgendaEvent,
  CalendarAgendaSlot,
  AgendaSection,
  FlatAgendaItem,
} from '@/types/calendar-ui';
import { LocalDateFormatter } from '@/lib/formatters/LocalDateFormatter';

const EVENT_CARD_HEIGHT = 96;
const EVENT_CARD_SPACING = 12;
const SLOT_VERTICAL_PADDING = 24;

/**
 * CalendarEventFormatter
 *
 * Provides static methods for formatting calendar events
 * and building agenda sections for UI display.
 */
export class CalendarEventFormatter {
  /**
   * Format time string (24h) to 12h format with AM/PM
   * @param time - Time string in HH:MM format (24h)
   * @returns Formatted time string (12h with AM/PM)
   */
  static formatTimeSlotLabel(time?: string | null): string {
    if (!time) return 'No time';

    const [hoursStr, minutes = '00'] = time.split(':');
    const hours = Number(hoursStr);
    if (Number.isNaN(hours)) return time;

    const suffix = hours >= 12 ? 'PM' : 'AM';
    const hour12 = ((hours + 11) % 12) + 1;
    return `${hour12}:${minutes}${suffix}`;
  }

  /**
   * Format event time range for display
   * @param event - Agenda event (or any CalendarEvent — `allDay` is used when `isAllDay` is missing)
   * @returns Formatted time range string
   */
  static formatEventTimeRange(event: CalendarAgendaEvent | CalendarEvent): string {
    const isAllDay = 'isAllDay' in event ? event.isAllDay : event.allDay;
    if (isAllDay) return 'All day';

    const start = event.startTime
      ? this.formatTimeSlotLabel(event.startTime)
      : '';
    const end = event.endTime ? this.formatTimeSlotLabel(event.endTime) : '';

    if (start && end) return `${start} - ${end}`;
    if (start) return start;
    if (end) return `Until ${end}`;
    return 'Time not set';
  }

  /**
   * Format full event date and time range for details display
   * @param event - Calendar event
   * @returns Formatted date and time range string
   */
  static formatEventDateRange(event: CalendarEvent): string {
    const startDate = new Date(event.startDate);
    const endDate = event.endDate ? new Date(event.endDate) : startDate;

    // Check if it's a multi-day event
    const isMultiDay = event.startDate !== event.endDate;

    // Format start date
    const startDateStr = startDate.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: startDate.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
    });

    // All-day events
    if (event.allDay || (!event.startTime && !event.endTime)) {
      if (isMultiDay) {
        const endDateStr = endDate.toLocaleDateString(undefined, {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          year: endDate.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
        });
        return `${startDateStr} - ${endDateStr}`;
      }
      return startDateStr;
    }

    // Timed events
    const startTimeStr = event.startTime
      ? this.formatTimeSlotLabel(event.startTime)
      : '';
    const endTimeStr = event.endTime
      ? this.formatTimeSlotLabel(event.endTime)
      : '';

    if (isMultiDay) {
      // Multi-day timed event
      const endDateStr = endDate.toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: endDate.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
      });
      return `${startDateStr} ${startTimeStr} - ${endDateStr} ${endTimeStr}`;
    }

    // Single-day timed event
    return `${startDateStr}, ${startTimeStr} - ${endTimeStr}`;
  }

  /**
   * Format date string for section title
   * @param date - ISO date string (YYYY-MM-DD)
   * @returns Formatted date string for display
   */
  static formatSectionTitle(date: string): string {
    try {
      const parsed = new Date(date);
      if (Number.isNaN(parsed.getTime())) {
        return date;
      }

      const currentYear = new Date().getFullYear();
      const eventYear = parsed.getFullYear();

      // Show year only if different from current year
      return parsed.toLocaleDateString(undefined, {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
        year: eventYear !== currentYear ? 'numeric' : undefined,
      });
    } catch {
      return date;
    }
  }

  /**
   * Convert CalendarEvent to CalendarAgendaEvent
   * @param event - Calendar event
   * @returns Agenda event for UI (includes all CalendarEvent fields)
   */
  static toAgendaEvent(event: CalendarEvent): CalendarAgendaEvent {
    return {
      ...event,
      eventId: event.id,
      isAllDay: event.allDay === true || (!event.startTime && !event.endTime),
    };
  }

  /**
   * Build agenda sections from calendar events
   * Groups events by date, then by time slot
   * @param events - Array of calendar events
   * @returns Array of agenda sections sorted by date
   */
  static buildAgendaSections(events: CalendarEvent[]): AgendaSection[] {
    // Group events by date
    const grouped = new Map<string, CalendarAgendaEvent[]>();

    events.forEach((event) => {
      const sectionKey = event.startDate;
      const agendaEvent = this.toAgendaEvent(event);

      if (!grouped.has(sectionKey)) {
        grouped.set(sectionKey, []);
      }
      grouped.get(sectionKey)!.push(agendaEvent);
    });

    // Build sections with time slots
    return Array.from(grouped.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, eventsForDay]) => {
        const slotsMap = new Map<
          string,
          {
            timeKey: string;
            timeLabel: string;
            events: CalendarAgendaEvent[];
          }
        >();

        // Sort events within the day
        eventsForDay
          .sort((a, b) => {
            // All-day events first
            if (a.isAllDay && !b.isAllDay) return -1;
            if (!a.isAllDay && b.isAllDay) return 1;

            // Then by start time
            const timeA = a.startTime ?? '';
            const timeB = b.startTime ?? '';
            return timeA.localeCompare(timeB);
          })
          .forEach((event) => {
            const timeKey = event.isAllDay
              ? 'ALL_DAY'
              : event.startTime ?? 'NO_TIME';
            const timeLabel = event.isAllDay
              ? 'All day'
              : this.formatTimeSlotLabel(event.startTime);

            const existing = slotsMap.get(timeKey);
            if (existing) {
              existing.events.push(event);
            } else {
              slotsMap.set(timeKey, {
                timeKey,
                timeLabel,
                events: [event],
              });
            }
          });

        // Sort time slots
        const slots = Array.from(slotsMap.values()).sort((a, b) => {
          if (a.timeKey === 'ALL_DAY') return -1;
          if (b.timeKey === 'ALL_DAY') return 1;
          if (a.timeKey === 'NO_TIME') return 1;
          if (b.timeKey === 'NO_TIME') return -1;
          return a.timeKey.localeCompare(b.timeKey);
        });

        // Build slot entries with heights
        const slotEntries: CalendarAgendaSlot[] = slots.map((slot) => ({
          name: slot.timeKey,
          day: date,
          height:
            slot.events.length * EVENT_CARD_HEIGHT +
            Math.max(slot.events.length - 1, 0) * EVENT_CARD_SPACING +
            SLOT_VERTICAL_PADDING,
          timeKey: slot.timeKey,
          timeLabel: slot.timeLabel,
          events: slot.events,
        }));

        return {
          title: date,
          data: slotEntries,
        };
      });
  }

  /**
   * Build flat agenda list from calendar events for custom FlashList
   * Flattens the nested structure into a single array with type markers
   * @param events - Array of calendar events
   * @returns Flat array of agenda items (headers, slots, events)
   */
  static buildFlatAgendaList(events: CalendarEvent[]): FlatAgendaItem[] {
    const flatItems: FlatAgendaItem[] = [];

    // Group events by date
    const grouped = new Map<string, CalendarAgendaEvent[]>();

    events.forEach((event) => {
      const sectionKey = event.startDate;
      const agendaEvent = this.toAgendaEvent(event);

      if (!grouped.has(sectionKey)) {
        grouped.set(sectionKey, []);
      }
      grouped.get(sectionKey)!.push(agendaEvent);
    });

    // Build flat list with date headers, time slots, and events
    Array.from(grouped.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([date, eventsForDay]) => {
        // Add date header
        flatItems.push({
          type: 'date-header',
          date,
          dateKey: `header-${date}`,
        });

        // Group events by time slot
        const slotsMap = new Map<
          string,
          {
            timeKey: string;
            timeLabel: string;
            events: CalendarAgendaEvent[];
          }
        >();

        // Sort events within the day
        eventsForDay
          .sort((a, b) => {
            // All-day events first
            if (a.isAllDay && !b.isAllDay) return -1;
            if (!a.isAllDay && b.isAllDay) return 1;

            // Then by start time
            const timeA = a.startTime ?? '';
            const timeB = b.startTime ?? '';
            return timeA.localeCompare(timeB);
          })
          .forEach((event) => {
            const timeKey = event.isAllDay
              ? 'ALL_DAY'
              : event.startTime ?? 'NO_TIME';
            const timeLabel = event.isAllDay
              ? 'All day'
              : this.formatTimeSlotLabel(event.startTime);

            const existing = slotsMap.get(timeKey);
            if (existing) {
              existing.events.push(event);
            } else {
              slotsMap.set(timeKey, {
                timeKey,
                timeLabel,
                events: [event],
              });
            }
          });

        // Sort time slots
        const slots = Array.from(slotsMap.values()).sort((a, b) => {
          if (a.timeKey === 'ALL_DAY') return -1;
          if (b.timeKey === 'ALL_DAY') return 1;
          if (a.timeKey === 'NO_TIME') return 1;
          if (b.timeKey === 'NO_TIME') return -1;
          return a.timeKey.localeCompare(b.timeKey);
        });

        // Add time slots and events
        slots.forEach((slot) => {
          // Add time slot item
          flatItems.push({
            type: 'time-slot',
            date,
            timeKey: slot.timeKey,
            timeLabel: slot.timeLabel,
          });

          // Add event items
          slot.events.forEach((event) => {
            flatItems.push({
              type: 'event',
              date,
              timeKey: slot.timeKey,
              event,
            });
          });
        });
      });

    return flatItems;
  }

  /**
   * Convert date and time strings to a Date object.
   * Used to build instanceStartDate for recurring event operations.
   */
  static toDate(dateStr: string, timeStr?: string | null): Date {
    const [year, month, day] = dateStr.split('-').map(Number);
    if (timeStr) {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return new Date(year, month - 1, day, hours, minutes, 0, 0);
    }
    return new Date(year, month - 1, day, 0, 0, 0, 0);
  }

  /**
   * Get today's date string in local timezone format
   * @returns Today's date (YYYY-MM-DD)
   */
  static getTodayDateString(): string {
    return LocalDateFormatter.getTodayDateString();
  }
}
